const DEFAULT_STORY37_MEDIA_HOST = "100.100.1.8";
const DEFAULT_STORY37_MEDIA_PORT = "3701";
export const HTTPS_PRODUCTION_HOST = "glc.cama-shade.ts.net";
const NAMU_IMAGE_ORIGIN = "http://100.100.1.17:5100";

function configuredMediaBaseUrl() {
  return (process.env.STORY37_MEDIA_BASE_URL || process.env.NEXT_PUBLIC_STORY37_MEDIA_BASE_URL || "").trim();
}

function hostnameFromRequestHost(requestHost?: string | null) {
  const value = (requestHost || "").trim();
  if (!value) {
    return "";
  }
  try {
    return new URL(`http://${value}`).hostname;
  } catch {
    return value.replace(/:\d+$/, "");
  }
}

function portFromRequestHost(requestHost?: string | null) {
  const value = (requestHost || "").trim();
  if (!value) {
    return "";
  }
  try {
    return String(new URL(`http://${value}`).port || "");
  } catch {
    const match = value.match(/:(\d+)$/);
    return match ? match[1] : "";
  }
}

function bracketHostname(hostname: string) {
  return hostname.includes(":") && !hostname.startsWith("[") ? `[${hostname}]` : hostname;
}

export function story37MediaBaseUrl(requestHost?: string | null) {
  const configured = configuredMediaBaseUrl();
  if (configured) {
    return configured.replace(/\/+$/, "");
  }
  const hostname = hostnameFromRequestHost(requestHost) || DEFAULT_STORY37_MEDIA_HOST;
  const requestPort = portFromRequestHost(requestHost);
  // 인증서 hostname에서는 정적 자산도 같은 HTTPS origin으로 내보내 Mixed Content를 막는다.
  if (hostname === HTTPS_PRODUCTION_HOST || (requestPort === "3703" && ["100.100.1.8", "192.168.1.8"].includes(hostname))) {
    return `//${bracketHostname(hostname)}${requestPort ? `:${requestPort}` : ""}`;
  }
  const port = DEFAULT_STORY37_MEDIA_PORT;
  return `//${bracketHostname(hostname)}:${port}`;
}

// 스태틱 미디어 base가 운영 nginx(443/3702/3703 — /reel-static 별칭을 서빙)인지 여부.
// 3701 개발 스태틱 서버는 루트가 /data/story37이라 같은 트리를 /reel 직접 경로로 준다.
export function isProductionMediaOrigin(baseUrl: string) {
  return baseUrl.includes(HTTPS_PRODUCTION_HOST) || /:(3702|3703)(\/|$)/.test(baseUrl);
}

export function shotAssetFile(shotId: number, ext: "mp4" | "webp") {
  return `${shotId.toString().padStart(6, "0")}.${ext}`;
}

function withVersion(url: string, version?: number | string | null) {
  return version === undefined || version === null || version === "" ? url : `${url}?v=${version}`;
}

export function shotVideoUrl(
  videoId: number,
  shotId: number,
  version?: number | string | null,
  mediaBaseUrl?: string | null
) {
  const baseUrl = (mediaBaseUrl || story37MediaBaseUrl()).replace(/\/+$/, "");
  return withVersion(`${baseUrl}/${videoId}/shots/${shotAssetFile(shotId, "mp4")}`, version);
}

export function verticalShotVideoUrl(
  videoId: number,
  shotId: number,
  version?: number | string | null,
  mediaBaseUrl?: string | null
) {
  const baseUrl = (mediaBaseUrl || story37MediaBaseUrl()).replace(/\/+$/, "");
  return withVersion(`${baseUrl}/${videoId}/shots_v/${shotAssetFile(shotId, "mp4")}`, version);
}

export function shotThumbUrl(
  videoId: number,
  shotId: number,
  version?: number | string | null,
  mediaBaseUrl?: string | null
) {
  const baseUrl = (mediaBaseUrl || story37MediaBaseUrl()).replace(/\/+$/, "");
  return withVersion(
    `${baseUrl}/${videoId}/frames/thumbs/${shotAssetFile(shotId, "webp")}`,
    version
  );
}

export function story37StaticAssetUrl(
  prefix: string,
  relativePath: string,
  version?: number | string | null,
  mediaBaseUrl?: string | null
) {
  const baseUrl = (mediaBaseUrl || story37MediaBaseUrl()).replace(/\/+$/, "");
  const cleanPrefix = prefix.replace(/^\/+|\/+$/g, "");
  const cleanPath = relativePath
    .split("/")
    .filter((part) => part.length > 0)
    .map((part) => encodeURIComponent(part))
    .join("/");
  return withVersion(`${baseUrl}/${cleanPrefix}/${cleanPath}`, version);
}

// nginx(static port)에 노출한 원본 소스 프리픽스. 서버 설정의 alias와 짝을 이룬다.
export const ORIGINAL_SOURCE_URL_PREFIX = "/__source";
// show_video.src_path가 실제로 존재하는 루트만 허용한다(임의 경로 노출 방지).
export const ORIGINAL_SOURCE_ROOTS = ["/data/", "/video1/", "/video2/", "/video3/"];

export function isServableOriginalSourcePath(value?: string | null) {
  const path = String(value || "").trim();
  if (!path.startsWith("/") || path.includes("..")) {
    return false;
  }
  return ORIGINAL_SOURCE_ROOTS.some((root) => path.startsWith(root));
}

export function originalSourceVideoUrl(srcPath?: string | null, mediaBaseUrl?: string | null) {
  const path = String(srcPath || "").trim();
  if (!isServableOriginalSourcePath(path)) {
    return "";
  }
  const baseUrl = (mediaBaseUrl || story37MediaBaseUrl()).replace(/\/+$/, "");
  const encoded = path
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
  return `${baseUrl}${ORIGINAL_SOURCE_URL_PREFIX}${encoded}`;
}

export function browserSafeImageUrl(value?: string | null) {
  const source = (value || "").trim();
  if (!source) {
    return null;
  }
  try {
    const url = new URL(source);
    if (url.origin !== NAMU_IMAGE_ORIGIN || !url.pathname.startsWith("/i/")) {
      return source;
    }
    const safePath = url.pathname
      .split("/")
      .filter(Boolean)
      .map((part) => encodeURIComponent(decodeURIComponent(part)))
      .join("/");
    return `/api/namu-image/${safePath}`;
  } catch {
    return source;
  }
}
